import React, { useState } from 'react';
import { faBars, faTimes, faUser, faCog } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation, useNavigate } from 'react-router-dom';
import ProfileDropdown from '../components/ProfileDropdown';
import { useAuth } from '../hooks/useAuth';
import MobileMenu from './MobileMenu';
import MobileProfileMenu from './MobileProfileMenu';
import OrganizationSelector from './OrganizationSelector';
import IconButton from './IconButton';
import { ADMIN_NAVIGATION, REGULAR_NAVIGATION, getPageIcon } from '../shared/navigation';

export default function Header(): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [profileOpen, setProfileOpen] = useState<boolean>(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);

  function handleLogout(): void {
    logout();
    navigate('/login');
  }

  // Check if we're in admin context
  const isAdminContext = location.pathname.startsWith('/admin');

  // Choose navigation based on context
  const navigation = isAdminContext ? ADMIN_NAVIGATION : REGULAR_NAVIGATION;

  return (
    <>
      {/* Desktop Header */}
      <header className="hidden lg:block bg-white shadow-sm border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-6">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg">
                <FontAwesomeIcon
                  icon={getPageIcon(location.pathname)}
                  className="w-5 h-5 text-blue"
                />
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <ProfileDropdown />
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Header */}
      <header className="lg:hidden bg-white border-b border-gray-200 shadow-sm">
        <div className="px-4 sm:px-6">
          <div className="flex justify-between h-16">
            <div className="flex w-full items-center space-x-4">
              <IconButton
                icon={mobileMenuOpen ? faTimes : faBars}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              />
              <div className="flex items-center w-full space-x-2 flex-grow min-w-0">
                {!isAdminContext ? (
                  <OrganizationSelector />
                ) : (
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center justify-center w-8 h-8 bg-red-100 rounded-lg">
                      <FontAwesomeIcon icon={faCog} className="w-4 h-4 text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">Admin SaaS</h3>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                className="flex items-center justify-center w-8 h-8 bg-blue-600 rounded-full"
              >
                <FontAwesomeIcon icon={faUser} className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        </div>

        <MobileMenu
          navigation={navigation}
          isOpen={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          onLogout={handleLogout}
        />

        <MobileProfileMenu isOpen={profileOpen} onLogout={handleLogout} />
      </header>
    </>
  );
}
